import type { MetadataInspection } from '../types'

const ascii = (bytes: Uint8Array, start: number, length: number): string =>
  String.fromCharCode(...bytes.subarray(start, start + length))

function readUint16BE(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) * 256 + (bytes[offset + 1] ?? 0)
}

function readUint32BE(bytes: Uint8Array, offset: number): number {
  return (((bytes[offset] ?? 0) * 0x1000000) + ((bytes[offset + 1] ?? 0) << 16) + ((bytes[offset + 2] ?? 0) << 8) + (bytes[offset + 3] ?? 0)) >>> 0
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.byteLength
  }
  return result
}

function inspectJpeg(bytes: Uint8Array): MetadataInspection {
  const findings: string[] = []
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return { format: 'unknown', clean: false, findings: ['JPEGの開始マーカーが不正です'] }
  let offset = 2
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) { offset += 1; continue }
    const marker = bytes[offset + 1] ?? 0
    offset += 2
    if (marker === 0xd9 || marker === 0xda) break
    if (marker === 0x00 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue
    const length = readUint16BE(bytes, offset)
    if (length < 2 || offset + length > bytes.length) {
      findings.push('JPEGセグメント長が不正です')
      break
    }
    const payloadStart = offset + 2
    const payloadLength = length - 2
    if (marker === 0xe1) {
      const header = ascii(bytes, payloadStart, Math.min(payloadLength, 32))
      findings.push(header.startsWith('Exif') ? 'EXIFセグメント' : header.includes('http://ns.adobe.com/xap') ? 'XMPセグメント' : 'APP1メタデータ')
    } else if (marker === 0xe2) {
      const header = ascii(bytes, payloadStart, Math.min(payloadLength, 32))
      // Canvas encoders may add a standard ICC color profile. It is not copied
      // from the input image and does not contain EXIF/GPS/device identity.
      if (!header.startsWith('ICC_PROFILE')) findings.push('APP2メタデータ')
    } else if (marker === 0xed) {
      findings.push('IPTCまたはAPP13メタデータ')
    } else if (marker === 0xfe) {
      findings.push('JPEGコメント')
    }
    offset += length
  }
  return { format: 'jpeg', clean: findings.length === 0, findings }
}

function inspectPng(bytes: Uint8Array): MetadataInspection {
  const findings: string[] = []
  const signature = [137, 80, 78, 71, 13, 10, 26, 10]
  if (!signature.every((value, index) => bytes[index] === value)) return { format: 'unknown', clean: false, findings: ['PNG署名が不正です'] }
  let offset = 8
  let width: number | undefined
  let height: number | undefined
  while (offset + 12 <= bytes.length) {
    const length = readUint32BE(bytes, offset)
    const type = ascii(bytes, offset + 4, 4)
    const dataStart = offset + 8
    if (dataStart + length + 4 > bytes.length) {
      findings.push('PNGチャンク長が不正です')
      break
    }
    if (type === 'IHDR' && length >= 8) {
      width = readUint32BE(bytes, dataStart)
      height = readUint32BE(bytes, dataStart + 4)
    }
    // iCCP is a color profile that a browser encoder may generate anew.
    // Privacy-bearing source metadata is not copied by the canvas pipeline.
    if (['eXIf', 'tEXt', 'zTXt', 'iTXt', 'tIME'].includes(type)) findings.push(`PNG ${type}メタデータ`)
    offset = dataStart + length + 4
    if (type === 'IEND') break
  }
  return { format: 'png', clean: findings.length === 0, findings, ...(width !== undefined ? { width } : {}), ...(height !== undefined ? { height } : {}) }
}

function inspectWebp(bytes: Uint8Array): MetadataInspection {
  const findings: string[] = []
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') return { format: 'unknown', clean: false, findings: ['WebP署名が不正です'] }
  let offset = 12
  while (offset + 8 <= bytes.length) {
    const type = ascii(bytes, offset, 4)
    const length = ((bytes[offset + 4] ?? 0) | ((bytes[offset + 5] ?? 0) << 8) | ((bytes[offset + 6] ?? 0) << 16) | ((bytes[offset + 7] ?? 0) << 24)) >>> 0
    if (offset + 8 + length > bytes.length) {
      findings.push('WebPチャンク長が不正です')
      break
    }
    if (['EXIF', 'XMP '].includes(type)) findings.push(`WebP ${type.trim()}メタデータ`)
    offset += 8 + length + (length % 2)
  }
  return { format: 'webp', clean: findings.length === 0, findings }
}

function stripJpegMetadata(bytes: Uint8Array): Uint8Array {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return bytes.slice()
  const chunks: Uint8Array[] = [bytes.slice(0, 2)]
  let offset = 2

  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) {
      chunks.push(bytes.slice(offset))
      break
    }

    const markerStart = offset
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1
    if (offset >= bytes.length) {
      chunks.push(bytes.slice(markerStart))
      break
    }

    const marker = bytes[offset] ?? 0
    offset += 1

    // Start of scan: copy the scan header, entropy-coded data and EOI verbatim.
    if (marker === 0xda) {
      chunks.push(bytes.slice(markerStart))
      break
    }

    if (marker === 0xd9) {
      chunks.push(bytes.slice(markerStart, offset))
      break
    }

    if (marker === 0x00 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      chunks.push(bytes.slice(markerStart, offset))
      continue
    }

    if (offset + 2 > bytes.length) return bytes.slice()
    const length = readUint16BE(bytes, offset)
    if (length < 2 || offset + length > bytes.length) return bytes.slice()

    const payloadStart = offset + 2
    const payloadLength = length - 2
    const segmentEnd = offset + length
    const header = ascii(bytes, payloadStart, Math.min(payloadLength, 32))
    const privacyMetadata = marker === 0xe1
      || marker === 0xed
      || marker === 0xfe
      || (marker === 0xe2 && !header.startsWith('ICC_PROFILE'))

    if (!privacyMetadata) chunks.push(bytes.slice(markerStart, segmentEnd))
    offset = segmentEnd
  }

  return concatBytes(chunks)
}

function stripPngMetadata(bytes: Uint8Array): Uint8Array {
  const signature = [137, 80, 78, 71, 13, 10, 26, 10]
  if (!signature.every((value, index) => bytes[index] === value)) return bytes.slice()
  const chunks: Uint8Array[] = [bytes.slice(0, 8)]
  let offset = 8

  while (offset + 12 <= bytes.length) {
    const length = readUint32BE(bytes, offset)
    const type = ascii(bytes, offset + 4, 4)
    const dataStart = offset + 8
    const chunkEnd = dataStart + length + 4
    if (chunkEnd > bytes.length) return bytes.slice()
    if (!['eXIf', 'tEXt', 'zTXt', 'iTXt', 'tIME'].includes(type)) {
      chunks.push(bytes.slice(offset, chunkEnd))
    }
    offset = chunkEnd
    if (type === 'IEND') break
  }

  if (offset < bytes.length) chunks.push(bytes.slice(offset))
  return concatBytes(chunks)
}

function stripWebpMetadata(bytes: Uint8Array): Uint8Array {
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') return bytes.slice()
  const chunks: Uint8Array[] = []
  let offset = 12

  while (offset + 8 <= bytes.length) {
    const type = ascii(bytes, offset, 4)
    const length = ((bytes[offset + 4] ?? 0) | ((bytes[offset + 5] ?? 0) << 8) | ((bytes[offset + 6] ?? 0) << 16) | ((bytes[offset + 7] ?? 0) << 24)) >>> 0
    const chunkEnd = offset + 8 + length + (length % 2)
    if (chunkEnd > bytes.length) return bytes.slice()
    if (!['EXIF', 'XMP '].includes(type)) chunks.push(bytes.slice(offset, chunkEnd))
    offset = chunkEnd
  }

  if (offset !== bytes.length) return bytes.slice()
  const bodyLength = 4 + chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0)
  const result = new Uint8Array(8 + bodyLength)
  result.set([82, 73, 70, 70], 0)
  const riffSize = bodyLength
  result[4] = riffSize & 255
  result[5] = (riffSize >>> 8) & 255
  result[6] = (riffSize >>> 16) & 255
  result[7] = (riffSize >>> 24) & 255
  result.set([87, 69, 66, 80], 8)
  let writeOffset = 12
  for (const chunk of chunks) {
    result.set(chunk, writeOffset)
    writeOffset += chunk.byteLength
  }
  return result
}

export function stripKnownImageMetadataBytes(bytes: Uint8Array): Uint8Array {
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return stripJpegMetadata(bytes)
  if (bytes[0] === 137 && bytes[1] === 80) return stripPngMetadata(bytes)
  if (ascii(bytes, 0, Math.min(12, bytes.length)).startsWith('RIFF') && ascii(bytes, 8, 4) === 'WEBP') return stripWebpMetadata(bytes)
  return bytes.slice()
}

export async function stripKnownImageMetadata(blob: Blob): Promise<Blob> {
  const stripped = stripKnownImageMetadataBytes(new Uint8Array(await blob.arrayBuffer()))
  const owned = new Uint8Array(stripped.byteLength)
  owned.set(stripped)
  return new Blob([owned.buffer], { type: blob.type })
}

export function inspectImageMetadataBytes(bytes: Uint8Array): MetadataInspection {
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return inspectJpeg(bytes)
  if (bytes[0] === 137 && bytes[1] === 80) return inspectPng(bytes)
  if (ascii(bytes, 0, Math.min(12, bytes.length)).startsWith('RIFF') && ascii(bytes, 8, 4) === 'WEBP') return inspectWebp(bytes)
  return { format: 'unknown', clean: false, findings: ['検査対象外の画像形式です'] }
}

export async function inspectImageMetadata(blob: Blob): Promise<MetadataInspection> {
  return inspectImageMetadataBytes(new Uint8Array(await blob.arrayBuffer()))
}
